import { getAuthToken } from "./authApi";
import { isSidebarQuote } from "./quoteStatus";

export const PRODUCTS_API_URL = "https://uktomars.co.uk/api/products";
export const CATEGORIES_API_URL = "https://uktomars.co.uk/api/categories";
export const BRANDS_API_URL = "https://uktomars.co.uk/api/brands";
export const EVENTS_API_URL = "https://uktomars.co.uk/api/events";
export const QUOTE_API_URL = "https://uktomars.co.uk/api/quotes";

function formatQuoteErrorMessage(message, status) {
  const text = String(message || "").trim();
  if (!text) {
    return status === 401
      ? "Your session has expired. Please sign in again."
      : "Unable to submit quote request. Please try again.";
  }

  const lower = text.toLowerCase();
  if (
    lower.includes("smtp") ||
    lower.includes("authenticator") ||
    lower.includes("535 5.7.8") ||
    lower.includes("mailer")
  ) {
    return (
      "Your quote could not be completed because the server email account " +
      "(sales@uktomars.co.uk) is not configured correctly. " +
      "Please contact sales@uktomars.co.uk with your basket details, " +
      "or ask your site administrator to update the API mail settings."
    );
  }

  return text;
}

export async function submitQuote({ note = "", lines }) {
  const token = getAuthToken();
  if (!token) {
    const error = new Error(
      "Please sign in to your trade account to request a quote.",
    );
    error.code = "AUTH_REQUIRED";
    throw error;
  }

  const response = await fetch(QUOTE_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ note, lines }),
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const rawMessage = payload?.message || payload?.error || "";
    const message = formatQuoteErrorMessage(rawMessage, response.status);
    const error = new Error(message);
    error.status = response.status;
    error.rawMessage = rawMessage;
    if (response.status === 401) {
      error.code = "AUTH_REQUIRED";
    }
    throw error;
  }

  return payload;
}

export async function fetchQuotes({ page = 1 } = {}) {
  const token = getAuthToken();
  if (!token) {
    const error = new Error("Please sign in to view your quote history.");
    error.code = "AUTH_REQUIRED";
    throw error;
  }

  const url =
    page > 1
      ? `${QUOTE_API_URL}?page=${encodeURIComponent(page)}`
      : QUOTE_API_URL;

  const response = await fetch(url, {
    cache: "no-store",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message =
      payload?.message ||
      payload?.error ||
      (response.status === 401
        ? "Your session has expired. Please sign in again."
        : "Unable to load quote history. Please try again.");
    const error = new Error(message);
    error.status = response.status;
    if (response.status === 401) {
      error.code = "AUTH_REQUIRED";
    }
    throw error;
  }

  return payload;
}

export async function fetchAllUserQuotes() {
  const all = [];
  let page = 1;
  let lastPage = 1;

  while (page <= lastPage) {
    const payload = await fetchQuotes({ page });
    const data = Array.isArray(payload?.data) ? payload.data : [];
    all.push(...data);
    lastPage = payload?.meta?.last_page ?? 1;
    page += 1;
  }

  return all.map(normalizeQuoteLines);
}

export async function fetchQuote(quoteId) {
  const token = getAuthToken();
  if (!token) {
    const error = new Error("Please sign in to view this quote.");
    error.code = "AUTH_REQUIRED";
    throw error;
  }

  const id = String(quoteId ?? "").trim();
  if (!id) {
    throw new Error("Quote not found.");
  }

  const response = await fetch(`${QUOTE_API_URL}/${encodeURIComponent(id)}`, {
    cache: "no-store",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message =
      payload?.message ||
      payload?.error ||
      (response.status === 404
        ? "Quote not found."
        : response.status === 401
          ? "Your session has expired. Please sign in again."
          : "Unable to load quote details. Please try again.");
    const error = new Error(message);
    error.status = response.status;
    if (response.status === 401) {
      error.code = "AUTH_REQUIRED";
    }
    throw error;
  }

  return normalizeQuoteLines(payload?.data ?? payload);
}

export function getActiveQuoteLines(lines) {
  return (Array.isArray(lines) ? lines : []).filter(
    (line) => (Number(line?.quantity) || 0) > 0,
  );
}

export function normalizeQuoteLines(quote) {
  if (!quote) return quote;
  return {
    ...quote,
    lines: getActiveQuoteLines(quote.lines),
  };
}

function quoteAuthHeaders(token) {
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };
}

function formatQuoteUpdateError(payload, response) {
  const rawMessage = payload?.message || payload?.error || "";
  if (response.status === 405 || response.status === 404) {
    return (
      rawMessage ||
      "Quote editing is not available on the server yet. Please ask your administrator to enable quote line update routes."
    );
  }
  if (response.status === 403) {
    return rawMessage || "This quote can no longer be edited.";
  }
  if (response.status === 401) {
    return rawMessage || "Your session has expired. Please sign in again.";
  }
  return (
    formatQuoteErrorMessage(rawMessage, response.status) ||
    "Unable to update quote line. Please try again."
  );
}

function normalizeQuoteLineId(lineId) {
  const id = Number(lineId);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("Quote line not found.");
  }
  return id;
}

function buildQuoteLinesPayload(lines, { allowZeroQuantity = false } = {}) {
  const normalized = (Array.isArray(lines) ? lines : [])
    .map((line) => ({
      id: line?.id,
      quantity: Number(line?.quantity) || 0,
    }))
    .filter((line) => line.id != null);

  if (allowZeroQuantity) {
    return normalized;
  }

  return normalized.filter((line) => line.quantity > 0);
}

export async function updateQuote(
  quoteId,
  { lines, note, status, allowZeroQuantity = false } = {},
) {
  const token = getAuthToken();
  if (!token) {
    const error = new Error("Please sign in to update this quote.");
    error.code = "AUTH_REQUIRED";
    throw error;
  }

  const id = String(quoteId ?? "").trim();
  if (!id) {
    throw new Error("Quote not found.");
  }

  const body = {};
  if (lines != null) {
    body.lines = buildQuoteLinesPayload(lines, { allowZeroQuantity });
  }
  if (note !== undefined) {
    body.note = note;
  }
  if (status !== undefined && status !== null && String(status).trim() !== "") {
    body.status = String(status).toLowerCase().trim();
  }

  const response = await fetch(`${QUOTE_API_URL}/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: quoteAuthHeaders(token),
    body: JSON.stringify(body),
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = formatQuoteUpdateError(payload, response);
    const error = new Error(message);
    error.status = response.status;
    if (response.status === 401) {
      error.code = "AUTH_REQUIRED";
    }
    throw error;
  }

  return normalizeQuoteLines(payload?.data ?? payload);
}

/** Update quote line quantities only — never sends status. */
export async function updateQuoteLines(
  quoteId,
  lines,
  { allowZeroQuantity = false } = {},
) {
  return updateQuote(quoteId, { lines, allowZeroQuantity });
}

export async function updateQuoteLineQuantity(quoteId, lineId, quantity) {
  const nextQuantity = Number(quantity);
  if (!Number.isFinite(nextQuantity) || nextQuantity < 1) {
    throw new Error("Quantity must be at least 1.");
  }

  return updateQuoteLines(quoteId, [{ id: lineId, quantity: nextQuantity }]);
}

/** Update one line — sends only that line in the PATCH body. */
export async function updateSingleQuoteLine(quote, lineId, nextQuantity) {
  return updateQuoteLineQuantity(quote.id, lineId, nextQuantity);
}

/**
 * PATCH /quotes/{id} — accept quote only.
 * Body: { "status": "accepted" }
 */
export async function acceptQuote(quoteId) {
  return updateQuote(quoteId, { status: "accepted" });
}

function isQuantityMinValidationError(error) {
  const message = String(error?.message || "").toLowerCase();
  return message.includes("at least 1") || message.includes("minimum");
}

export async function deleteQuoteLine(quoteId, lineId) {
  const normalizedLineId = normalizeQuoteLineId(lineId);

  try {
    return await updateQuote(quoteId, {
      lines: [{ id: normalizedLineId, quantity: 0 }],
      allowZeroQuantity: true,
    });
  } catch (error) {
    if (!isQuantityMinValidationError(error)) {
      throw error;
    }

    const quote = await fetchQuote(quoteId);
    const lines = getActiveQuoteLines(quote?.lines).filter(
      (line) => Number(line.id) !== normalizedLineId,
    );

    return updateQuote(quoteId, { lines });
  }
}

export async function fetchPendingQuotes() {
  const quotes = await fetchAllUserQuotes();
  return quotes.filter(isSidebarQuote);
}

const SHOP_CACHE_TTL_MS = 5 * 60 * 1000;
const SHOP_FETCH_MAX_ATTEMPTS = 6;
const PRODUCT_PAGE_SIZE = 24;
const PRODUCT_BULK_PAGE_SIZE = 100;

const shopResponseCache = new Map();
const shopInflight = new Map();
const shopListCache = new Map();

function sleep(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function retryDelayMs(attempt, retryAfterHeader) {
  const fromHeader = Number.parseInt(retryAfterHeader, 10);
  if (Number.isFinite(fromHeader) && fromHeader > 0) {
    return Math.min(fromHeader * 1000, 15000);
  }
  return Math.min(800 * 2 ** attempt, 8000);
}

function isRetryableStatus(status) {
  return status === 429 || status === 503 || status === 502;
}

export async function fetchShopJson(url, { ttlMs = SHOP_CACHE_TTL_MS } = {}) {
  const key = String(url);
  const cached = shopResponseCache.get(key);
  if (cached && cached.expires > Date.now()) {
    return cached.data;
  }

  const existing = shopInflight.get(key);
  if (existing) return existing;

  const request = (async () => {
    let lastError;
    for (let attempt = 0; attempt < SHOP_FETCH_MAX_ATTEMPTS; attempt += 1) {
      try {
        const response = await fetch(key, {
          headers: { Accept: "application/json" },
        });

        if (isRetryableStatus(response.status)) {
          lastError = new Error(`Failed to fetch ${key}`);
          lastError.status = response.status;
          await sleep(
            retryDelayMs(attempt, response.headers.get("Retry-After")),
          );
          continue;
        }

        if (!response.ok) {
          const error = new Error(`Failed to fetch ${key}`);
          error.status = response.status;
          throw error;
        }

        const data = await response.json();
        if (ttlMs > 0) {
          shopResponseCache.set(key, {
            expires: Date.now() + ttlMs,
            data,
          });
        }
        return data;
      } catch (error) {
        lastError = error;
        if (error?.status && !isRetryableStatus(error.status)) {
          throw error;
        }
        if (attempt < SHOP_FETCH_MAX_ATTEMPTS - 1) {
          await sleep(retryDelayMs(attempt));
        }
      }
    }

    throw lastError || new Error(`Failed to fetch ${key}`);
  })().finally(() => {
    shopInflight.delete(key);
  });

  shopInflight.set(key, request);
  return request;
}

function hasProductFilterParams(searchParams) {
  return ["brand_id", "q", "category", "search"].some((key) =>
    Boolean(searchParams.get(key)),
  );
}

function isUnfilteredProductsUrl(url) {
  try {
    const parsed = new URL(url, PRODUCTS_API_URL);
    const path = parsed.pathname.replace(/\/+$/, "");
    if (!path.endsWith("/products")) return false;
    return !hasProductFilterParams(parsed.searchParams);
  } catch {
    return false;
  }
}

async function collectAllPages(url) {
  const parsed = new URL(url, PRODUCTS_API_URL);
  if (!parsed.searchParams.has("per_page")) {
    parsed.searchParams.set("per_page", String(PRODUCT_BULK_PAGE_SIZE));
  }

  const all = [];
  let page = Number(parsed.searchParams.get("page") || 1) || 1;
  let lastPage = 1;

  do {
    parsed.searchParams.set("page", String(page));
    const payload = await fetchShopJson(parsed.toString());
    const data = Array.isArray(payload?.data) ? payload.data : [];
    all.push(...data);

    const metaLast = Number(payload?.meta?.last_page);
    if (!Number.isFinite(metaLast) || metaLast < 1) {
      break;
    }

    if (page === 1 && metaLast > 1 && isUnfilteredProductsUrl(url)) {
      throw new Error(
        "Refusing to download the full product catalog. Load products page-by-page.",
      );
    }

    lastPage = metaLast;
    page += 1;
  } while (page <= lastPage);

  return all;
}

export async function fetchAllPaginated(url) {
  const key = `all:${url}`;
  const cached = shopListCache.get(key);
  if (cached && cached.expires > Date.now()) {
    return cached.data;
  }

  const existing = shopInflight.get(key);
  if (existing) return existing;

  const request = collectAllPages(url)
    .then((data) => {
      shopListCache.set(key, {
        expires: Date.now() + SHOP_CACHE_TTL_MS,
        data,
      });
      return data;
    })
    .finally(() => {
      shopInflight.delete(key);
    });

  shopInflight.set(key, request);
  return request;
}

export async function fetchAllBrands() {
  const payload = await fetchShopJson(BRANDS_API_URL);
  return Array.isArray(payload?.data) ? payload.data : [];
}

export const getProductsByBrandUrl = (brandId) => {
  const id = String(brandId ?? "").trim();
  if (!id) return "";
  const params = new URLSearchParams({
    brand_id: id,
    per_page: String(PRODUCT_BULK_PAGE_SIZE),
  });
  return `${PRODUCTS_API_URL}?${params.toString()}`;
};

export const fetchProductsByBrand = async (brandId) => {
  const url = getProductsByBrandUrl(brandId);
  if (!url) return [];
  return fetchAllPaginated(url);
};

export const findBrandById = async (brandId) => {
  const id = String(brandId ?? "").trim();
  if (!id) return null;

  const brands = await fetchAllBrands();
  return brands.find((brand) => String(brand?.id) === id) ?? null;
};

export const getBrandProductsPath = (brand) => {
  if (brand?.id == null) return "/Brands";
  const id = encodeURIComponent(String(brand.id));
  return `/Brands/${id}/products?brand_id=${id}`;
};

export const getProductsListUrl = ({
  category = "",
  subcategory = "",
  q = "",
  brandId = "",
  page = 1,
  perPage = PRODUCT_PAGE_SIZE,
} = {}) => {
  const params = new URLSearchParams();
  if (subcategory) {
    params.set("category", subcategory);
  } else if (category) {
    params.set("category", category);
  }
  if (q) params.set("q", String(q).trim());
  if (brandId) params.set("brand_id", String(brandId));
  params.set("page", String(page || 1));
  params.set("per_page", String(perPage || PRODUCT_PAGE_SIZE));
  return `${PRODUCTS_API_URL}?${params.toString()}`;
};

export async function fetchProductsPage({
  category = "",
  subcategory = "",
  q = "",
  brandId = "",
  page = 1,
  perPage = PRODUCT_PAGE_SIZE,
} = {}) {
  const url = getProductsListUrl({
    category,
    subcategory,
    q,
    brandId,
    page,
    perPage,
  });
  const payload = await fetchShopJson(url);
  const data = Array.isArray(payload?.data) ? payload.data : [];
  return {
    data,
    meta: payload?.meta ?? {},
  };
}

export async function searchProducts(query, { perPage = 10 } = {}) {
  const q = String(query ?? "").trim();
  if (!q) return [];
  const { data } = await fetchProductsPage({ q, page: 1, perPage });
  return data.filter((product) => product?.is_active !== false);
}

export const getCategoryProductsPath = (categorySlug) =>
  `/products?category=${encodeURIComponent(categorySlug)}`;
